import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * Le journal de diagnostic des tablettes (D15) — ce que le terrain **consigne** en local et
 * remonte par lots quand il a du réseau. Ce n'est pas de la donnée métier : rien ici ne
 * modifie une demande, une fiche ou une validation. C'est la seule télémétrie du système, et
 * elle transporte des **mesures et des identifiants, jamais de donnée personnelle** — ni nom,
 * ni téléphone, ni coordonnée GPS (le lieu d'une signature reste dans la proposition, M20).
 */
export const CLIENT_LOG_LEVELS = ['info', 'warn', 'error'];
export const clientLogLevelSchema = z.enum(CLIENT_LOG_LEVELS);
/**
 * Une ligne telle que la tablette l'a écrite. `loose` comme le relevé (M16) : une tablette
 * en avance sur l'API le temps d'un déploiement ne doit pas perdre son journal pour un champ
 * de trop.
 *
 * `deviceId` + `sessionId` + `seq` identifient la ligne **chez l'émetteur** : c'est le
 * triplet qui rend le rejeu d'un lot inoffensif côté serveur (`UNIQUE`, conflits ignorés) —
 * même contrat que la clé d'idempotence du dépôt (D4).
 */
export const clientLogEntrySchema = z.looseObject({
    deviceId: uuidV7Schema,
    sessionId: uuidV7Schema,
    seq: z.number().int().min(0),
    at: z.iso.datetime({ offset: true }),
    level: clientLogLevelSchema,
    /** Le préfixe de convention des consoles du dépôt : `[app]`, `[sync]`, `[api]`… */
    tag: z.string().min(1).max(32),
    /** Le nom machine stable de l'événement (`app.boot`, `sync.sent`, `api.call`…). */
    event: z.string().min(1).max(64),
    message: z.string().max(4000),
    /** Le contexte chiffré, déjà borné côté tablette — `unknown` : c'est du jsonb, pas un contrat. */
    detail: z.unknown().nullish(),
    inquiryId: uuidV7Schema.nullish(),
    /** L'horodatage de build du bundle émetteur — ce qui distingue deux versions déployées. */
    appBuild: z.string().max(64).nullish(),
});
/**
 * Un lot borné : 200 lignes suffisent à vider un tampon en quelques passes, et le plafond —
 * avec la limite de corps de la route — borne ce qu'un jeton égaré peut verser (même
 * raisonnement que `MAX_MEDIA_PER_INSPECTION`).
 */
export const MAX_CLIENT_LOGS_PER_BATCH = 200;
export const clientLogBatchSchema = z.object({
    entries: z.array(clientLogEntrySchema).min(1).max(MAX_CLIENT_LOGS_PER_BATCH),
});
/**
 * Ce que le serveur a fait du lot. `accepted` compte les lignes **réellement insérées** :
 * un rejeu complet rend `0`, et c'est un succès — la tablette marque tout expédié.
 */
export const clientLogShipResultSchema = z.object({
    accepted: z.number().int().min(0),
});
//# sourceMappingURL=client-log.js.map